/**
 * Generated by @openapi-codegen
 *
 * @version 1.0.0
 */
import * as reactQuery from 'app/src/shared/helpers/reactquery'
import { RefundContext, useRefundContext } from './refund-context'
import type * as Fetcher from './refund-fetcher'
import { refundFetch } from './refund-fetcher'
import type * as Responses from './refund-responses'
import type * as Schemas from './refund-schemas'

export type RefundRequestListQueryParams = {
  /**
   * Posizione primo elemento da ritornare per la pagina di risultati corrente
   *
   * @format int32
   * @default 0
   * @minimum 0
   */
  pagingOffset?: number
  /**
   * Numero di righe massimo per la pagina di risultati
   *
   * @format int32
   * @default 20
   * @minimum 1
   * @maximum 50
   */
  pagingLimit?: number
  /**
   * Start date for the refund creation period.
   *
   * @format date-time
   */
  from?: string
  /**
   * End date for the refund creation period.
   *
   * @format date-time
   */
  to?: string
  /**
   * Sort order for the refund listings, +date (ascending) or -date (descending).
   *
   * @default -date
   */
  sort?: '+date' | '-date'
  /**
   * The membership ID used to filter refunds
   */
  membershipId?: string
  /**
   * The status list of the refund request, semicolon separated.
   */
  status?: Schemas.RefundStatusValue[]
}

export type RefundRequestListHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
}

export type RefundRequestListError = Fetcher.ErrorWrapper<{
  status: 400
  payload: Responses.ErrorBadRequest
}>

export type RefundRequestListVariables = {
  headers?: RefundRequestListHeaders
  queryParams?: RefundRequestListQueryParams
} & RefundContext['fetcherOptions']

/**
 * Returns the list of the Refund request
 */
export const fetchRefundRequestList = (variables: RefundRequestListVariables, signal?: AbortSignal) =>
  refundFetch<
    Schemas.RefundPaginated,
    RefundRequestListError,
    undefined,
    RefundRequestListHeaders,
    RefundRequestListQueryParams,
    {}
  >({ url: '/refunds', method: 'get', ...variables, signal })

/**
 * Returns the list of the Refund request
 */
export const useRefundRequestList = <TData = Schemas.RefundPaginated>(
  variables: RefundRequestListVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.RefundPaginated, RefundRequestListError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useRefundContext(options)
  return reactQuery.useQuery<Schemas.RefundPaginated, RefundRequestListError, TData>({
    queryKey: queryKeyFn({
      path: '/refunds',
      operationId: 'refundRequestList',
      variables,
    }),
    queryFn: ({ signal }) => fetchRefundRequestList({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type RefundRequestsDeleteQueryParams = {
  /**
   * The ids of the refund requests to delete, semicolon separated.
   */
  ids: string[]
}

export type RefundRequestsDeleteHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestsDeleteError = Fetcher.ErrorWrapper<{
  status: 400
  payload: Responses.ErrorBadRequest
}>

export type RefundRequestsDeleteVariables = {
  headers?: RefundRequestsDeleteHeaders
  queryParams: RefundRequestsDeleteQueryParams
} & RefundContext['fetcherOptions']

/**
 * Deletes the refund requests specified by the ids. It is possible to delete only the refund requests in draft. The ids are semicolon separated. The maximum number of ids is 100.
 */
export const fetchRefundRequestsDelete = (variables: RefundRequestsDeleteVariables, signal?: AbortSignal) =>
  refundFetch<undefined, RefundRequestsDeleteError, undefined, RefundRequestsDeleteHeaders, RefundRequestsDeleteQueryParams, {}>({
    url: '/refunds',
    method: 'delete',
    ...variables,
    signal,
  })

/**
 * Deletes the refund requests specified by the ids. It is possible to delete only the refund requests in draft. The ids are semicolon separated. The maximum number of ids is 100.
 */
export const useRefundRequestsDelete = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, RefundRequestsDeleteError, RefundRequestsDeleteVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<undefined, RefundRequestsDeleteError, RefundRequestsDeleteVariables>({
    mutationFn: (variables: RefundRequestsDeleteVariables) => fetchRefundRequestsDelete({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type RefundRequestCreateHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestCreateError = Fetcher.ErrorWrapper<{
  status: 400
  payload: Responses.ErrorBadRequest
}>

export type RefundRequestCreateVariables = {
  body: Schemas.RefundCreateRequest
  headers?: RefundRequestCreateHeaders
} & RefundContext['fetcherOptions']

/**
 * New refund request
 */
export const fetchRefundRequestCreate = (variables: RefundRequestCreateVariables, signal?: AbortSignal) =>
  refundFetch<Schemas.Refund, RefundRequestCreateError, Schemas.RefundCreateRequest, RefundRequestCreateHeaders, {}, {}>({
    url: '/refunds',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * New refund request
 */
export const useRefundRequestCreate = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.Refund, RefundRequestCreateError, RefundRequestCreateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<Schemas.Refund, RefundRequestCreateError, RefundRequestCreateVariables>({
    mutationFn: (variables: RefundRequestCreateVariables) => fetchRefundRequestCreate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type RefundRequestGetPathParams = {
  refundId: string
}

export type RefundRequestGetHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestGetError = Fetcher.ErrorWrapper<{
  status: 404
  payload: Responses.ErrorEntityNotFound
}>

export type RefundRequestGetVariables = {
  headers?: RefundRequestGetHeaders
  pathParams: RefundRequestGetPathParams
} & RefundContext['fetcherOptions']

/**
 * Returns the details of the Refund request
 */
export const fetchRefundRequestGet = (variables: RefundRequestGetVariables, signal?: AbortSignal) =>
  refundFetch<Schemas.Refund, RefundRequestGetError, undefined, RefundRequestGetHeaders, {}, RefundRequestGetPathParams>({
    url: '/refunds/{refundId}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the details of the Refund request
 */
export const useRefundRequestGet = <TData = Schemas.Refund>(
  variables: RefundRequestGetVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.Refund, RefundRequestGetError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useRefundContext(options)
  return reactQuery.useQuery<Schemas.Refund, RefundRequestGetError, TData>({
    queryKey: queryKeyFn({
      path: '/refunds/{refundId}',
      operationId: 'refundRequestGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchRefundRequestGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type RefundRequestDeletePathParams = {
  refundId: string
}

export type RefundRequestDeleteHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestDeleteError = Fetcher.ErrorWrapper<{
  status: 400
  payload: Responses.ErrorBadRequest
}>

export type RefundRequestDeleteVariables = {
  headers?: RefundRequestDeleteHeaders
  pathParams: RefundRequestDeletePathParams
} & RefundContext['fetcherOptions']

/**
 * Deletes the refund request specified by the id. It is possible to delete only the refund requests in draft.
 */
export const fetchRefundRequestDelete = (variables: RefundRequestDeleteVariables, signal?: AbortSignal) =>
  refundFetch<undefined, RefundRequestDeleteError, undefined, RefundRequestDeleteHeaders, {}, RefundRequestDeletePathParams>({
    url: '/refunds/{refundId}',
    method: 'delete',
    ...variables,
    signal,
  })

/**
 * Deletes the refund request specified by the id. It is possible to delete only the refund requests in draft.
 */
export const useRefundRequestDelete = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, RefundRequestDeleteError, RefundRequestDeleteVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<undefined, RefundRequestDeleteError, RefundRequestDeleteVariables>({
    mutationFn: (variables: RefundRequestDeleteVariables) => fetchRefundRequestDelete({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type RefundRequestConfirmPathParams = {
  refundId: string
}

export type RefundRequestConfirmHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestConfirmError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorEntityConflict
    }
>

export type RefundRequestConfirmVariables = {
  body?: Schemas.RefundConfirmRequest
  headers?: RefundRequestConfirmHeaders
  pathParams: RefundRequestConfirmPathParams
} & RefundContext['fetcherOptions']

/**
 * Confirm a refund request. If the fields 'invoice', 'documents', and 'requestedTotal' are provided, the current refund value will be replaced with these latest updates.
 */
export const fetchRefundRequestConfirm = (variables: RefundRequestConfirmVariables, signal?: AbortSignal) =>
  refundFetch<
    Schemas.RefundConfirmResponse,
    RefundRequestConfirmError,
    Schemas.RefundConfirmRequest,
    RefundRequestConfirmHeaders,
    {},
    RefundRequestConfirmPathParams
  >({
    url: '/refunds/{refundId}/confirm',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Confirm a refund request. If the fields 'invoice', 'documents', and 'requestedTotal' are provided, the current refund value will be replaced with these latest updates.
 */
export const useRefundRequestConfirm = (
  options?: Omit<
    reactQuery.UseMutationOptions<Schemas.RefundConfirmResponse, RefundRequestConfirmError, RefundRequestConfirmVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<Schemas.RefundConfirmResponse, RefundRequestConfirmError, RefundRequestConfirmVariables>({
    mutationFn: (variables: RefundRequestConfirmVariables) => fetchRefundRequestConfirm({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type RefundRequestDocumentAssociatePathParams = {
  refundId: string
}

export type RefundRequestDocumentAssociateHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestDocumentAssociateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorEntityConflict
    }
>

export type RefundRequestDocumentAssociateVariables = {
  body: Schemas.DocumentAssociateRequest
  headers?: RefundRequestDocumentAssociateHeaders
  pathParams: RefundRequestDocumentAssociatePathParams
} & RefundContext['fetcherOptions']

/**
 * Associate one or more document to a refund request.
 */
export const fetchRefundRequestDocumentAssociate = (variables: RefundRequestDocumentAssociateVariables, signal?: AbortSignal) =>
  refundFetch<
    undefined,
    RefundRequestDocumentAssociateError,
    Schemas.DocumentAssociateRequest,
    RefundRequestDocumentAssociateHeaders,
    {},
    RefundRequestDocumentAssociatePathParams
  >({
    url: '/refunds/{refundId}/document',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Associate one or more document to a refund request.
 */
export const useRefundRequestDocumentAssociate = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, RefundRequestDocumentAssociateError, RefundRequestDocumentAssociateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<undefined, RefundRequestDocumentAssociateError, RefundRequestDocumentAssociateVariables>({
    mutationFn: (variables: RefundRequestDocumentAssociateVariables) =>
      fetchRefundRequestDocumentAssociate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type RefundRequestInvoiceAssociatePathParams = {
  refundId: string
}

export type RefundRequestInvoiceAssociateHeaders = {
  /**
   * The tenant of the logged in user
   */
  ['X-Tenant']?: string
  /**
   * The user id of the logged in user
   */
  ['X-User']?: string
  /**
   * The membership id of the logged in user
   */
  ['X-MembershipId']?: string
}

export type RefundRequestInvoiceAssociateError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
  | {
      status: 409
      payload: Responses.ErrorEntityConflict
    }
>

export type RefundRequestInvoiceAssociateVariables = {
  body?: Schemas.AssociateInvoice
  headers?: RefundRequestInvoiceAssociateHeaders
  pathParams: RefundRequestInvoiceAssociatePathParams
} & RefundContext['fetcherOptions']

/**
 * Associate the invoice data to a refund request.
 */
export const fetchRefundRequestInvoiceAssociate = (variables: RefundRequestInvoiceAssociateVariables, signal?: AbortSignal) =>
  refundFetch<
    undefined,
    RefundRequestInvoiceAssociateError,
    Schemas.AssociateInvoice,
    RefundRequestInvoiceAssociateHeaders,
    {},
    RefundRequestInvoiceAssociatePathParams
  >({
    url: '/refunds/{refundId}/invoice',
    method: 'put',
    ...variables,
    signal,
  })

/**
 * Associate the invoice data to a refund request.
 */
export const useRefundRequestInvoiceAssociate = (
  options?: Omit<
    reactQuery.UseMutationOptions<undefined, RefundRequestInvoiceAssociateError, RefundRequestInvoiceAssociateVariables>,
    'mutationFn'
  >,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<undefined, RefundRequestInvoiceAssociateError, RefundRequestInvoiceAssociateVariables>({
    mutationFn: (variables: RefundRequestInvoiceAssociateVariables) =>
      fetchRefundRequestInvoiceAssociate({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type HookRefundStatusError = Fetcher.ErrorWrapper<
  | {
      status: 400
      payload: Responses.ErrorBadRequest
    }
  | {
      status: 404
      payload: Responses.ErrorEntityNotFound
    }
>

export type HookRefundStatusVariables = {
  body: Schemas.RefundStatusHookRequest
} & RefundContext['fetcherOptions']

/**
 * Handle the update of a refund status
 */
export const fetchHookRefundStatus = (variables: HookRefundStatusVariables, signal?: AbortSignal) =>
  refundFetch<undefined, HookRefundStatusError, Schemas.RefundStatusHookRequest, {}, {}, {}>({
    url: '/hook/refund/status',
    method: 'post',
    ...variables,
    signal,
  })

/**
 * Handle the update of a refund status
 */
export const useHookRefundStatus = (
  options?: Omit<reactQuery.UseMutationOptions<undefined, HookRefundStatusError, HookRefundStatusVariables>, 'mutationFn'>,
) => {
  const { fetcherOptions } = useRefundContext()
  return reactQuery.useMutation<undefined, HookRefundStatusError, HookRefundStatusVariables>({
    mutationFn: (variables: HookRefundStatusVariables) => fetchHookRefundStatus({ ...fetcherOptions, ...variables }),
    ...options,
  })
}

export type RefundConfigurationListError = Fetcher.ErrorWrapper<undefined>

export type RefundConfigurationListVariables = RefundContext['fetcherOptions']

/**
 * Returns the full set of configurable options applicable for initiating a specific Refund process.
 */
export const fetchRefundConfigurationList = (variables: RefundConfigurationListVariables, signal?: AbortSignal) =>
  refundFetch<Schemas.RefundConfResponse, RefundConfigurationListError, undefined, {}, {}, {}>({
    url: '/refunds/conf',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * Returns the full set of configurable options applicable for initiating a specific Refund process.
 */
export const useRefundConfigurationList = <TData = Schemas.RefundConfResponse>(
  variables: RefundConfigurationListVariables,
  options?: Omit<
    reactQuery.UseQueryOptions<Schemas.RefundConfResponse, RefundConfigurationListError, TData>,
    'queryKey' | 'queryFn' | 'initialData'
  >,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useRefundContext(options)
  return reactQuery.useQuery<Schemas.RefundConfResponse, RefundConfigurationListError, TData>({
    queryKey: queryKeyFn({
      path: '/refunds/conf',
      operationId: 'refundConfigurationList',
      variables,
    }),
    queryFn: ({ signal }) => fetchRefundConfigurationList({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type RefundRuleGetPathParams = {
  /**
   * Denotes the type of the specific refund rules being queried.
   */
  kind: string
}

export type RefundRuleGetError = Fetcher.ErrorWrapper<{
  status: 404
  payload: Responses.ErrorEntityNotFound
}>

export type RefundRuleGetVariables = {
  pathParams: RefundRuleGetPathParams
} & RefundContext['fetcherOptions']

/**
 * -> This endpoint retrieves a specific refund rule identified by the 'kind' parameter. The 'kind' specifies the type of the refund rule, and the response includes details of the corresponding rule.
 */
export const fetchRefundRuleGet = (variables: RefundRuleGetVariables, signal?: AbortSignal) =>
  refundFetch<Schemas.Rule, RefundRuleGetError, undefined, {}, {}, RefundRuleGetPathParams>({
    url: '/refunds/conf/rules/{kind}',
    method: 'get',
    ...variables,
    signal,
  })

/**
 * -> This endpoint retrieves a specific refund rule identified by the 'kind' parameter. The 'kind' specifies the type of the refund rule, and the response includes details of the corresponding rule.
 */
export const useRefundRuleGet = <TData = Schemas.Rule>(
  variables: RefundRuleGetVariables,
  options?: Omit<reactQuery.UseQueryOptions<Schemas.Rule, RefundRuleGetError, TData>, 'queryKey' | 'queryFn' | 'initialData'>,
) => {
  const { fetcherOptions, queryOptions, queryKeyFn } = useRefundContext(options)
  return reactQuery.useQuery<Schemas.Rule, RefundRuleGetError, TData>({
    queryKey: queryKeyFn({
      path: '/refunds/conf/rules/{kind}',
      operationId: 'refundRuleGet',
      variables,
    }),
    queryFn: ({ signal }) => fetchRefundRuleGet({ ...fetcherOptions, ...variables }, signal),
    ...options,
    ...queryOptions,
  })
}

export type QueryOperation =
  | {
      path: '/refunds'
      operationId: 'refundRequestList'
      variables: RefundRequestListVariables
    }
  | {
      path: '/refunds/{refundId}'
      operationId: 'refundRequestGet'
      variables: RefundRequestGetVariables
    }
  | {
      path: '/refunds/conf'
      operationId: 'refundConfigurationList'
      variables: RefundConfigurationListVariables
    }
  | {
      path: '/refunds/conf/rules/{kind}'
      operationId: 'refundRuleGet'
      variables: RefundRuleGetVariables
    }
